import type { BrowserProfileBoxInfo, SessionObject, PremiumFeatures } from '@lub0v/santa-utils';

export interface GuestGame {
  name: string;
  key: string;
  premiumKey?: string | null;
  isArchived?: boolean;
  isPremium?: boolean;
  features?: PremiumFeatures;
}

function boxesForGuest(user: SessionObject): BrowserProfileBoxInfo[] {
  const own = user.profiles.find((profile) => profile.id === user.id);
  if (own) {
    return own.boxes;
  }
  return user.profiles.filter((profile) => profile.isGuest).flatMap((profile) => profile.boxes);
}

/** Boxes on the signed-in profile, or the guest’s own boxes. */
export function sessionBoxes(user: SessionObject | null): BrowserProfileBoxInfo[] {
  if (!user) {
    return [];
  }
  if (user.isGuest) {
    return boxesForGuest(user);
  }
  return (
    user.profiles.find((profile) => profile.id === user.id)?.boxes ??
    user.profiles.find((profile) => profile.active)?.boxes ??
    []
  );
}

/**
 * The one game a guest session exists for. A quick draw signs the organizer
 * in as a guest so they can keep that box; if they only have one, the header
 * can send them straight back to it. More than one, or none, is not a chip.
 */
export function guestOnlyGame(user: SessionObject | null): GuestGame | null {
  if (!user?.isGuest) {
    return null;
  }
  const withKey = boxesForGuest(user).filter(
    (box): box is BrowserProfileBoxInfo & { key: string } => Boolean(box.key),
  );
  if (withKey.length !== 1) {
    return null;
  }
  return {
    name: withKey[0].name,
    key: withKey[0].key,
    premiumKey: withKey[0].premiumKey,
    isArchived: withKey[0].isArchived,
    isPremium: withKey[0].isPremium,
    features: withKey[0].features,
  };
}
